import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { RefreshCcw } from 'lucide-react';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { AppSidebar } from '@/components/layout/app-sidebar';
import { Header } from '@/components/layout/header';
import { getOnboardingRequirement, redirectToOnboardingWhenRequired } from '@/features/onboarding/route-guards';
import { OrganizationLimitBanner } from '@/features/organizations/components/organization-limit-banner';
import { useCurrentOrganizationId } from '@/features/organizations/store/useCurrentOrganizationStore';
import { requireAuthenticatedRoute } from '@/lib/router-guards';

export const Route = createFileRoute('/_app')({
    beforeLoad: async () => {
        await requireAuthenticatedRoute();
        await redirectToOnboardingWhenRequired();
    },
    component: AppLayout
});

export function AppLayout() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const currentOrganizationId = useCurrentOrganizationId();
    const [verifiedOrganizationId, setVerifiedOrganizationId] = useState<string | null>(null);
    const [failedCheck, setFailedCheck] = useState<{ organizationId: string; attempt: number } | null>(null);
    const [retryAttempt, setRetryAttempt] = useState(0);
    const onboardingCheckIdRef = useRef(0);
    const isCheckingSelectedOrganization =
        currentOrganizationId !== null && verifiedOrganizationId !== currentOrganizationId;
    const selectedOrganizationCheckFailed =
        currentOrganizationId !== null &&
        failedCheck?.organizationId === currentOrganizationId &&
        failedCheck.attempt === retryAttempt;

    useEffect(() => {
        if (!currentOrganizationId) {
            onboardingCheckIdRef.current += 1;
            return;
        }

        const checkId = ++onboardingCheckIdRef.current;

        void getOnboardingRequirement()
            .then((requirement) => {
                if (checkId !== onboardingCheckIdRef.current) {
                    return;
                }

                if (requirement.status === 'redirect') {
                    void navigate({ to: requirement.target.to });
                    return;
                }

                if (requirement.status === 'clear' && requirement.organizationId === currentOrganizationId) {
                    setVerifiedOrganizationId(currentOrganizationId);
                    setFailedCheck(null);
                    return;
                }

                if (requirement.status === 'failed') {
                    setFailedCheck({ organizationId: currentOrganizationId, attempt: retryAttempt });
                }
            })
            .catch(() => {
                if (checkId === onboardingCheckIdRef.current) {
                    setFailedCheck({ organizationId: currentOrganizationId, attempt: retryAttempt });
                }
            });
    }, [currentOrganizationId, navigate, retryAttempt]);

    function retrySelectedOrganizationCheck() {
        setRetryAttempt((attempt) => attempt + 1);
    }

    return (
        <SidebarProvider style={{ '--sidebar-width': '15rem' } as CSSProperties}>
            <AppSidebar />
            <SidebarInset>
                <Header />
                <OrganizationLimitBanner />
                {isCheckingSelectedOrganization ? (
                    <AppOutletPendingState
                        hasFailed={selectedOrganizationCheckFailed}
                        retryLabel={t('appShell.onboardingCheck.retry')}
                        failureTitle={t('appShell.onboardingCheck.failureTitle')}
                        failureDescription={t('appShell.onboardingCheck.failureDescription')}
                        onRetry={retrySelectedOrganizationCheck}
                    />
                ) : (
                    <Outlet />
                )}
            </SidebarInset>
        </SidebarProvider>
    );
}

type AppOutletPendingStateProps = {
    hasFailed: boolean;
    retryLabel: string;
    failureTitle: string;
    failureDescription: string;
    onRetry: () => void;
};

function AppOutletPendingState({
    hasFailed,
    retryLabel,
    failureTitle,
    failureDescription,
    onRetry
}: AppOutletPendingStateProps) {
    return (
        <div className='flex flex-1 flex-col px-4 py-6 sm:px-6' aria-busy='true' data-testid='app-outlet-pending'>
            <div className='mx-auto flex w-full max-w-6xl flex-1 flex-col gap-6'>
                {hasFailed ? (
                    <div
                        className='rounded-lg border border-destructive/20 bg-destructive/6 p-4 text-sm text-destructive'
                        role='alert'
                        data-testid='app-outlet-onboarding-check-error'
                    >
                        <div className='font-medium'>{failureTitle}</div>
                        <p className='mt-1 text-destructive/80'>{failureDescription}</p>
                        <Button className='mt-4' variant='outline' size='sm' onClick={onRetry}>
                            <RefreshCcw className='size-4' aria-hidden='true' />
                            {retryLabel}
                        </Button>
                    </div>
                ) : null}
                <div className='space-y-3'>
                    <Skeleton className='h-8 w-52' />
                    <Skeleton className='h-4 w-80 max-w-full' />
                </div>
                <div className='grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]'>
                    <Skeleton className='h-48 w-full' />
                    <Skeleton className='h-48 w-full' />
                </div>
                <Skeleton className='h-72 w-full' />
            </div>
        </div>
    );
}
